import React, { FC, forwardRef } from 'react';
import {
  useMenuState,
  Menu as BaseMenu,
  MenuItem as BaseMenuItem,
  MenuButton,
  MenuSeparator,
  MenuDisclosureProps,
  MenuProps as RMenuProps,
  MenuItemProps,
} from 'reakit/Menu';
import { Portal } from 'reakit/Portal';
import classNames from 'classnames';
import { MenuContentWrapper, MenuItem } from './MenuComponents';

export type MenuProps = {
  menuProps: Partial<RMenuProps>;
  disclosure: React.ReactElement<Partial<MenuDisclosureProps>> & { ref?: React.Ref<unknown> };
  menuItems: React.ReactElement[];
  initialVisible?: boolean;
  closeMenuOnItemClick?: boolean;
  className?: string;
  ariaLabel?: string;
  portal?: boolean;
};

const AbstractMenu = forwardRef<MenuProps, any>(
  ({ className = '', ariaLabel, disclosure, menuItems, menuProps, initialVisible = false, closeMenuOnItemClick = true, portal = false, ...props }, ref) => {
    const menu = useMenuState({ visible: initialVisible });

    const menuContent = (
      <BaseMenu
        {...menu}
        aria-label={ariaLabel}
        {...menuProps}
        className={classNames([className, portal ? 'tw-fixed' : 'tw-absolute'])}
        as={MenuContentWrapper}
      >
        {menuItems.filter(Boolean).map((item: any, i: number) => {
          if (item.type === MenuSeparator) {
            return React.cloneElement(item, {
              ...menu,
              key: item.key || i,
              ...item.props,
            });
          }
          return (
            <BaseMenuItem
              {...menu}
              {...item.props}
              key={item.key || i}
              onClick={() => {
                if (item.props.onClick) {
                  item.props.onClick();
                }
                if (closeMenuOnItemClick) {
                  menu.hide();
                }
              }}
            >
              {(itemProps: MenuItemProps) => React.cloneElement(item, itemProps)}
            </BaseMenuItem>
          );
        })}
      </BaseMenu>
    );

    return (
      <>
        <MenuButton ref={ref} {...menu} {...props} {...disclosure.props}>
          {(disclosureProps: MenuDisclosureProps) => React.cloneElement(disclosure, disclosureProps)}
        </MenuButton>
        {portal ? <Portal>{menuContent}</Portal> : menuContent}
      </>
    );
  },
);

// The keyword `any` is used here as a workaround to avoid type errors
export const Menu: FC<React.PropsWithChildren<MenuProps>> & {
  MenuItem: typeof MenuItem;
} = AbstractMenu as any;

Menu.MenuItem = MenuItem;
